import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useUser } from '../../auth/AuthPage/UserContext';
import LoginGate from '../../common/LoginGate/LoginGate';
import ShopMap from '../../common/ShopMap/ShopMap';
import AddressInput from '../../common/AddressInput/AddressInput';
import ConfirmDialog from '../../common/ConfirmDialog/ConfirmDialog';
import { ReviewShop } from './ReviewShop';
import Icon from '../../common/Icon/Icon';
import './Shipping.css';

/**
 * Shipping flow - four gated chapters after a trade is accepted:
 *  1. DECIDE   - each side says middleman yes/no; both see the outcome
 *                and accept/deny it (either wanting one = both use one)
 *  2. SETUP    - each side submits middleman + delivery address; both
 *                review each other's setup and accept/deny
 *  3. DOCUMENT - each side photographs + describes their cards (final),
 *                reviews the other's docs and accepts; then each takes a
 *                PRIVATE photo of their sealed package, which unlocks
 *                the address
 *  4. SHIP     - senders ship, middlemen process from their dashboard,
 *                receivers mark received, THEN rating unlocks
 *
 * The page is one vertical path: chapters are numbered stops, and the
 * current chapter unfolds into single actions. Only one action is open
 * at a time; finished ones shrink to a line, and a "waiting" stop marks
 * every point where the other trader has to move next.
 */

const STEPS_SHOP = [
    ['ready', 'Ready to ship'],
    ['shipped', 'Dropped at middleman'],
    ['shop_received', 'Middleman received'],
    ['shop_verified', 'Contents verified'],
    ['forwarded', 'Forwarded'],
    ['delivered', 'Received'],
];
const STEPS_DIRECT = [
    ['ready', 'Ready to ship'],
    ['shipped', 'Shipped'],
    ['delivered', 'Received'],
];
// Pickup legs end at the receiver's own middleman. When that's the
// shop verifying the package, there's no forwarding at all
const STEPS_SHOP_PICKUP = [
    ['ready', 'Ready to ship'],
    ['shipped', 'Dropped at middleman'],
    ['shop_received', 'Middleman received'],
    ['shop_verified', 'Ready for pickup'],
    ['delivered', 'Picked up'],
];
// ...otherwise the verifying shop sends it on to the pickup shop
const STEPS_SHOP_PICKUP_FAR = [
    ['ready', 'Ready to ship'],
    ['shipped', 'Dropped at middleman'],
    ['shop_received', 'Middleman received'],
    ['shop_verified', 'Contents verified'],
    ['forwarded', 'Sent to your shop'],
    ['at_pickup', 'Ready for pickup'],
    ['delivered', 'Picked up'],
];
const STEP_ORDER = ['pending', 'ready', 'shipped', 'shop_received', 'shop_verified', 'forwarded', 'at_pickup', 'delivered'];
const VERIFIED_OR_LATER = ['shop_verified', 'forwarded', 'at_pickup', 'delivered'];

// Only the receiver's own view of a leg carries dest_type
const pickupElsewhere = leg => leg.dest_type === 'shop' && !!leg.dest_shop_id && leg.dest_shop_id !== leg.shop_id;
const legSteps = leg => (leg.method === 'direct' ? STEPS_DIRECT
    : leg.dest_type !== 'shop' ? STEPS_SHOP
        : pickupElsewhere(leg) ? STEPS_SHOP_PICKUP_FAR : STEPS_SHOP_PICKUP);

// One package's progress as a row of dots plus where it is now
function MiniTrack({ leg, label }) {
    if (!leg) return null;
    const steps = legSteps(leg);
    const reached = STEP_ORDER.indexOf(leg.status);
    const rejected = leg.status === 'shop_rejected';
    const now = rejected ? 'Rejected'
        : [...steps].reverse().find(([k]) => STEP_ORDER.indexOf(k) <= reached)?.[1];
    return (
        <div className={`trail-track${rejected ? ' rejected' : ''}`}>
            <span className="trail-track-label">{label}</span>
            <span className="trail-track-dots">
                {steps.map(([k, l]) => (
                    <i key={k} title={l} className={STEP_ORDER.indexOf(k) <= reached ? 'on' : ''} />
                ))}
            </span>
            <span className="trail-track-now">{now}</span>
        </div>
    );
}

function ShopCard({ shop }) {
    if (!shop) return null;
    return (
        <div className="ship-shopcard">
            <div className="ship-shopcard-head">
                <Link to={`/store/${shop.id}`} className="ship-shopcard-link"><strong>{shop.name}</strong></Link>
                <span className="shop-picker-rating">
                    {shop.rating_avg ? `★ ${shop.rating_avg} (${shop.rating_count})` : 'no ratings yet'}
                </span>
            </div>
            {shop.fee_info && <div className="ship-shopcard-fee">{shop.fee_info}</div>}
            {shop.address && <div className="ship-shopcard-addr">{shop.address}</div>}
        </div>
    );
}

// A chapter stop on the path: number, label, and a one-line result once done
function TrailChapter({ n, label, state, summary }) {
    return (
        <li className={`trail-item trail-chapter is-${state}`}>
            <span className="trail-marker">{state === 'done' ? '✓' : n}</span>
            <div className="trail-row">
                <span className="trail-chapter-label">{label}</span>
                {summary && <span className="trail-summary">{summary}</span>}
            </div>
        </li>
    );
}

// An action stop. done/upcoming = one line; active/wait = an open card
function TrailNode({ node }) {
    const { key, state, title, summary, change, body } = node;
    const open = state === 'active' || state === 'wait';
    return (
        <li className={`trail-item is-${state}`} data-key={key}>
            <span className="trail-marker">{state === 'done' ? '✓' : ''}</span>
            {open ? (
                <div className="trail-card">
                    {state === 'wait' && <span className="trail-dots" aria-hidden="true"><i /><i /><i /></span>}
                    <h3 className="trail-card-title">{title}</h3>
                    {body}
                    {state === 'wait' && <p className="trail-autonote">This page updates by itself.</p>}
                </div>
            ) : (
                <div className="trail-row">
                    <span className="trail-title">{title}</span>
                    {state === 'done' && summary && <span className="trail-summary">{summary}</span>}
                    {state === 'done' && change && (
                        <button className="trail-change" onClick={change.onClick}>{change.label}</button>
                    )}
                </div>
            )}
        </li>
    );
}

function Shipping() {
    const { tradeId } = useParams();
    const { user } = useUser();
    const navigate = useNavigate();

    const [data, setData] = useState(null);
    const [allShops, setAllShops] = useState([]);
    const [error, setError] = useState(null);
    const [busy, setBusy] = useState(false);
    const [draftShopId, setDraftShopId] = useState(null);
    const [draftAddress, setDraftAddress] = useState('');
    const [addrVerified, setAddrVerified] = useState(false);
    // Middleman trades only: pick incoming cards up AT the middleman
    // instead of having them forwarded to an address
    const [draftPickup, setDraftPickup] = useState(false);
    // Which draft actions the trader has said "next" on (setup + photos).
    // A done action can be reopened with "Change" until it's locked in.
    const [confirmed, setConfirmed] = useState({});
    const [rated, setRated] = useState({});
    const [binderCleaned, setBinderCleaned] = useState(false);
    // Styled dialogs instead of window.confirm/alert
    const [dialog, setDialog] = useState(null);  // {title,message,confirmLabel,danger,action}
    const [notice, setNotice] = useState(null);  // {title,message,onClose}
    // Documentation step drafts
    const [docFiles, setDocFiles] = useState([]);      // File objects (1-5)
    const [docDesc, setDocDesc] = useState('');
    const [pkgFile, setPkgFile] = useState(null);      // single package photo
    const [lightbox, setLightbox] = useState(null);    // photo url or null

    // Preview URLs made once per pick (not on every render), freed after
    const docPreviews = useMemo(() => docFiles.map(f => URL.createObjectURL(f)), [docFiles]);
    useEffect(() => () => docPreviews.forEach(u => URL.revokeObjectURL(u)), [docPreviews]);
    const pkgPreview = useMemo(() => (pkgFile ? URL.createObjectURL(pkgFile) : null), [pkgFile]);
    useEffect(() => () => { if (pkgPreview) URL.revokeObjectURL(pkgPreview); }, [pkgPreview]);

    const load = useCallback(async () => {
        try {
            const res = await fetch(`/api/trade-requests/${tradeId}/shipping`, { credentials: 'include' });
            const json = await res.json();
            if (!res.ok) throw new Error(json.error || 'Failed to load shipping.');
            // Only re-render when something actually changed, so the 6s
            // poll can never disturb a form someone is mid-typing in
            setData(prev => JSON.stringify(prev) === JSON.stringify(json) ? prev : json);
            setDraftShopId(prev => prev ?? json.setup?.mine?.shopId ?? null);
            setDraftAddress(prev => {
                if (prev) return prev;
                const saved = json.setup?.mine?.address || '';
                if (saved) setAddrVerified(true); // already accepted once
                return saved;
            });
            setError(null);
        } catch (e) {
            setError(e.message);
        }
    }, [tradeId]);

    useEffect(() => { load(); }, [load]);

    // Live updates: poll while the tab is visible so the other party's
    // moves (choices, accepts, shipping steps) appear without refreshing
    useEffect(() => {
        const id = setInterval(() => {
            if (!document.hidden) load();
        }, 6000);
        const onFocus = () => load();
        window.addEventListener('focus', onFocus);
        return () => {
            clearInterval(id);
            window.removeEventListener('focus', onFocus);
        };
    }, [load]);

    useEffect(() => {
        fetch('/api/shops')
            .then(r => (r.ok ? r.json() : { shops: [] }))
            .then(d => setAllShops(d.shops || []))
            .catch(() => { });
    }, []);

    // Account settings: prefill the saved delivery address (already
    // geocode-verified when it was saved) and the preferred middleman.
    // Prefilled answers count as confirmed, so the path skips past them.
    useEffect(() => {
        fetch('/api/user/settings', { credentials: 'include' })
            .then(r => (r.ok ? r.json() : null))
            .then(d => {
                if (!d?.settings) return;
                if (d.settings.default_address) {
                    setDraftAddress(prev => {
                        if (prev) return prev;
                        setAddrVerified(true);
                        setConfirmed(c => ({ ...c, address: true }));
                        return d.settings.default_address;
                    });
                }
                if (d.settings.preferred_shop_id) {
                    setDraftShopId(prev => {
                        if (prev) return prev;
                        setConfirmed(c => ({ ...c, shop: true }));
                        return d.settings.preferred_shop_id;
                    });
                }
            })
            .catch(() => { });
    }, []);

    // When the open action moves on, bring it into view if it's off screen
    const lastFocus = useRef(null);
    useEffect(() => {
        const el = document.querySelector('.trail-item.is-active, .trail-item.is-wait');
        const key = el?.dataset.key;
        if (!key) return;
        if (lastFocus.current && key !== lastFocus.current) {
            const r = el.getBoundingClientRect();
            if (r.top < 80 || r.top > window.innerHeight * 0.65) {
                el.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
        }
        lastFocus.current = key;
    });

    const post = async (path, body) => {
        setBusy(true);
        try {
            const res = await fetch(`/api/trade-requests/${tradeId}/shipping/${path}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify(body || {}),
            });
            const json = await res.json();
            if (!res.ok) throw new Error(json.error || 'Action failed.');
            if (json.cancelled) {
                setNotice({
                    title: 'Trade cancelled',
                    message: 'The other trader has been notified. The trade moves to your history.',
                    onClose: () => navigate('/requests'),
                });
                return;
            }
            await load();
        } catch (e) {
            setNotice({ title: 'Action failed', message: e.message });
        } finally {
            setBusy(false);
        }
    };

    // Multipart POST for photo uploads
    const postForm = async (path, formData) => {
        setBusy(true);
        try {
            const res = await fetch(`/api/trade-requests/${tradeId}/shipping/${path}`, {
                method: 'POST',
                credentials: 'include',
                body: formData,
            });
            const json = await res.json();
            if (!res.ok) throw new Error(json.error || 'Upload failed.');
            await load();
            return true;
        } catch (e) {
            setNotice({ title: 'Upload failed', message: e.message });
            return false;
        } finally {
            setBusy(false);
        }
    };

    const denyWithConfirm = (path) => {
        setDialog({
            title: 'Deny and cancel this trade?',
            message: 'The whole trade is scrapped, the other trader is notified, and it moves to history. This cannot be undone.',
            confirmLabel: 'Deny trade',
            danger: true,
            action: () => post(path, { accept: false }),
        });
    };

    if (!user) return <LoginGate title="Shipping" message="Log in to manage shipping." />;
    if (error) return <div className="requests-container"><div className="requests-placeholder">{error}</div></div>;
    if (!data) return <div className="requests-container"><div className="requests-placeholder">Loading shipping&hellip;</div></div>;
    // A middleman shop can open this page, but it is the traders' page:
    // the server sends no "mine" slot for a shop viewer, and a shop's
    // work (received, verified, forwarded) lives on its own dashboard.
    if (data.setup && data.setup.mine === null) {
        return (
            <div className="requests-container">
                <div className="requests-placeholder">
                    <p style={{ margin: '0 0 0.5rem' }}>You're the middleman on this trade, not one of the traders.</p>
                    <p style={{ margin: 0 }}>Handle its packages from <a href="/shop">your shop dashboard</a>.</p>
                </div>
            </div>
        );
    }

    const otherName = Object.entries(data.usernames).find(([id]) => Number(id) !== user.id)?.[1] || 'the other trader';
    const otherId = Number(Object.keys(data.usernames).find(id => Number(id) !== user.id)) || null;
    const shopById = id => data.shops[id] || allShops.find(s => s.id === id) || null;
    const finished = data.tradeStatus === 'finished';
    const dead = data.tradeStatus === 'cancelled';
    const outLeg = data.legs.find(l => l.sender_id === user.id);
    const inLeg = data.legs.find(l => l.receiver_id === user.id);

    const markRated = (shopId) => setRated(r => ({ ...r, [shopId]: true }));
    const ratableShopIds = [...new Set(data.legs.map(l => l.shop_id).filter(Boolean))];
    // The server remembers what's already wrapped up
    const isRated = (shopId) => !!rated[shopId] || (data.ratedShopIds || []).includes(shopId);
    const bindersDone = binderCleaned || !!data.binderCleaned;
    const yesNo = v => (v === 1 ? 'Yes' : 'No');
    const markConfirmed = key => setConfirmed(c => ({ ...c, [key]: true }));

    // Draft actions open in order: the first one not yet done is the open
    // card, everything after it waits its turn
    const makeFlow = (locked) => {
        let opened = false;
        return (done) => {
            if (done || locked) return 'done';
            if (!opened) { opened = true; return 'active'; }
            return 'upcoming';
        };
    };
    const reopener = (locked) => (key) => (locked || dead) ? null : {
        label: 'Change',
        onClick: () => setConfirmed(c => ({ ...c, [key]: false })),
    };

    const dropOut = () => {
        setDialog({
            title: 'Drop out of this trade?',
            message: 'The trade will be cancelled and the other trader notified.',
            confirmLabel: 'Drop out',
            danger: true,
            action: () => post('dropout'),
        });
    };

    const reviewButtons = (ackPath, label) => (
        <div className="trail-actions">
            <button className="cta-gold" disabled={busy || dead} onClick={() => post(ackPath, { accept: true })}>{label}</button>
            <button className="trail-deny" disabled={busy || dead} onClick={() => denyWithConfirm(ackPath)}>Deny trade</button>
        </div>
    );

    /* ---------------- CHAPTER 1 · DECIDE ---------------- */
    const decideNodes = () => {
        const { prefs } = data;
        const chose = prefs.mine !== null;
        return [
            {
                key: 'decide-choose',
                state: chose ? 'done' : 'active',
                title: 'Use a middleman?',
                summary: chose ? yesNo(prefs.mine) : null,
                // Free to switch until the other trader has answered too
                change: chose && !prefs.bothChosen && !dead ? {
                    label: `Switch to ${prefs.mine === 1 ? 'No' : 'Yes'}`,
                    onClick: () => post('prefs', { wantsMiddleman: prefs.mine !== 1 }),
                } : null,
                body: (
                    <>
                        <div className="trail-choices">
                            <button className="trail-choice" disabled={busy || dead}
                                onClick={() => post('prefs', { wantsMiddleman: true })}>
                                <strong>Yes</strong>
                                <span>A card shop checks both sides</span>
                            </button>
                            <button className="trail-choice" disabled={busy || dead}
                                onClick={() => post('prefs', { wantsMiddleman: false })}>
                                <strong>No</strong>
                                <span>Mail cards straight to each other</span>
                            </button>
                        </div>
                        <p className="trail-note">If either of you says yes, you both use one.</p>
                    </>
                ),
            },
            {
                key: 'decide-result',
                state: !chose ? 'upcoming' : !prefs.bothChosen ? 'wait' : !prefs.myAck ? 'active' : 'done',
                title: chose && !prefs.bothChosen ? `Waiting on ${otherName} to answer` : 'Agree on the result',
                summary: data.middlemanRequired ? 'Middleman trade' : 'Direct trade',
                body: prefs.bothChosen && (
                    <>
                        <div className="trail-outcome">{data.middlemanRequired ? 'Middleman trade' : 'Direct trade'}</div>
                        <p className="trail-note">
                            {data.middlemanRequired
                                ? 'You each drop your cards at a card shop.'
                                : 'You mail your cards straight to each other.'}
                            {' '}You said {yesNo(prefs.mine)}, {otherName} said {yesNo(prefs.theirs)}.
                        </p>
                        {reviewButtons('prefs/ack', 'Agree')}
                    </>
                ),
            },
            {
                key: 'decide-wait',
                state: prefs.myAck ? 'wait' : 'upcoming',
                title: prefs.myAck ? `Waiting on ${otherName} to agree` : `${otherName} agrees`,
            },
        ];
    };

    /* ---------------- CHAPTER 2 · SET UP ---------------- */
    const setupNodes = () => {
        const { setup, middlemanRequired: mm } = data;
        const locked = setup.myComplete;
        const pickup = locked ? setup.mine.pickup : mm && draftPickup;
        const flow = makeFlow(locked);
        const reopen = reopener(locked);
        const nodes = [];

        const submitFinal = () => {
            setDialog({
                title: 'Lock in your setup?',
                message: `Your ${mm ? 'middleman and ' : ''}${pickup ? 'pickup choice' : 'address'} can't be changed for this trade after this.`,
                confirmLabel: 'Lock in',
                action: () => post('setup', {
                    shopId: draftShopId,
                    address: pickup ? '' : draftAddress.trim(),
                    pickup,
                }),
            });
        };

        if (mm) {
            nodes.push({
                key: 'setup-shop',
                state: flow(confirmed.shop && !!draftShopId),
                title: 'Pick your middleman',
                summary: shopById(locked ? setup.mine.shopId : draftShopId)?.name,
                change: reopen('shop'),
                body: (
                    <>
                        <p className="trail-note">You'll drop your cards off here.</p>
                        <div className="ship-map-wrap">
                            <ShopMap
                                withSearch
                                shops={allShops}
                                selectedId={draftShopId}
                                onSelect={(s) => setDraftShopId(s.id)}
                                height={330}
                            />
                        </div>
                        {draftShopId && <div className="trail-picked"><ShopCard shop={shopById(draftShopId)} /></div>}
                        <div className="trail-actions">
                            <button className="cta-gold" disabled={!draftShopId} onClick={() => markConfirmed('shop')}>
                                Use this shop
                            </button>
                        </div>
                    </>
                ),
            });
            nodes.push({
                key: 'setup-mode',
                state: flow(!!confirmed.mode),
                title: 'How do you get your cards?',
                summary: pickup ? 'Pick them up' : 'Mailed to me',
                change: reopen('mode'),
                body: (
                    <div className="trail-choices">
                        <button className="trail-choice" onClick={() => { setDraftPickup(false); markConfirmed('mode'); }}>
                            <strong>Mail them to me</strong>
                            <span>The shop mails them to your address</span>
                        </button>
                        <button className="trail-choice" onClick={() => { setDraftPickup(true); markConfirmed('mode'); }}>
                            <strong>Pick them up</strong>
                            <span>At {shopById(locked ? setup.mine.shopId : draftShopId)?.name || 'your middleman'}, no address needed</span>
                        </button>
                    </div>
                ),
            });
        }

        if (!pickup) {
            nodes.push({
                key: 'setup-address',
                state: flow(confirmed.address && addrVerified && !!draftAddress.trim()),
                title: 'Your mailing address',
                summary: locked ? (setup.mine.resolved || setup.mine.address) : draftAddress,
                change: reopen('address'),
                body: (
                    <>
                        <AddressInput
                            address={draftAddress}
                            setAddress={setDraftAddress}
                            verified={addrVerified}
                            setVerified={setAddrVerified}
                        />
                        <p className="trail-note">
                            {mm ? 'Only the middleman mailing to you sees this.' : `Hidden from ${otherName} until it's time to ship.`}
                        </p>
                        <div className="trail-actions">
                            <button className="cta-gold" disabled={!addrVerified || !draftAddress.trim()}
                                onClick={() => markConfirmed('address')}>
                                Next
                            </button>
                        </div>
                    </>
                ),
            });
        }

        nodes.push({
            key: 'setup-lock',
            state: flow(false),
            title: 'Lock it in',
            summary: 'Locked',
            body: (
                <>
                    <p className="trail-note">Final for this trade.</p>
                    <div className="trail-actions">
                        <button className="cta-gold" disabled={busy || dead} onClick={submitFinal}>Lock in</button>
                    </div>
                </>
            ),
        });

        const theirs = setup.theirs;
        nodes.push({
            key: 'setup-review',
            state: !locked ? 'upcoming' : !setup.theirsComplete ? 'wait' : !setup.myAck ? 'active' : 'done',
            title: locked && !setup.theirsComplete ? `Waiting on ${otherName}'s setup`
                : mm ? `Check ${otherName}'s middleman` : `Check ${otherName}'s setup`,
            summary: 'Approved',
            body: theirs && (
                <>
                    {mm
                        ? <ShopCard shop={shopById(theirs.shopId)} />
                        : <p className="trail-fact">Their address is in. You'll see it when it's time to ship.</p>}
                    {reviewButtons('setup/ack', 'Looks good')}
                </>
            ),
        });

        nodes.push({
            key: 'setup-wait',
            state: setup.myAck ? 'wait' : 'upcoming',
            title: setup.myAck ? `Waiting on ${otherName} to approve yours` : `${otherName} approves yours`,
        });
        return nodes;
    };

    /* ---------------- CHAPTER 3 · PHOTOS (direct trades) ---------------- */
    const docsNodes = () => {
        const docs = data.docs || {};
        const mine = docs.mine || {};
        const locked = !!mine.submitted;
        const flow = makeFlow(locked);
        const reopen = reopener(locked);
        const thumbs = locked ? (mine.photos || []) : docPreviews;

        const submitDocs = () => {
            setDialog({
                title: 'Share with ' + otherName + '?',
                message: 'Your photos and description are final once shared.',
                confirmLabel: 'Share',
                action: () => {
                    const fd = new FormData();
                    docFiles.forEach(f => fd.append('photos', f));
                    fd.append('description', docDesc.trim());
                    postForm('docs', fd).then(ok => { if (ok) { setDocFiles([]); setDocDesc(''); } });
                },
            });
        };

        return [
            {
                key: 'docs-photos',
                state: flow(confirmed.photos && docFiles.length > 0),
                title: 'Photograph your cards',
                summary: (
                    <span className="trail-thumbs">
                        {thumbs.map(u => <img key={u} src={u} alt="" />)}
                    </span>
                ),
                change: reopen('photos'),
                body: (
                    <>
                        <label className="ship-file-drop">
                            <input type="file" accept="image/*" multiple
                                onChange={e => setDocFiles([...e.target.files].slice(0, 5))} />
                            {docFiles.length > 0
                                ? `${docFiles.length} photo${docFiles.length !== 1 ? 's' : ''} · tap to change`
                                : 'Choose up to 5 photos'}
                        </label>
                        {docPreviews.length > 0 && (
                            <div className="ship-photo-grid">
                                {docPreviews.map((u, i) => <img key={u} src={u} alt={`preview ${i + 1}`} />)}
                            </div>
                        )}
                        <div className="trail-actions">
                            <button className="cta-gold" disabled={docFiles.length < 1} onClick={() => markConfirmed('photos')}>Next</button>
                        </div>
                    </>
                ),
            },
            {
                key: 'docs-desc',
                state: flow(confirmed.desc && !!docDesc.trim()),
                title: 'Describe them',
                summary: locked ? mine.description : docDesc,
                change: reopen('desc'),
                body: (
                    <>
                        <textarea
                            className="ship-rate-comment ship-docs-desc"
                            placeholder="Card names, condition, foil or not"
                            value={docDesc}
                            maxLength={500}
                            rows={3}
                            onChange={e => setDocDesc(e.target.value)}
                        />
                        <div className="trail-actions">
                            <button className="cta-gold" disabled={!docDesc.trim()} onClick={() => markConfirmed('desc')}>Next</button>
                        </div>
                    </>
                ),
            },
            {
                key: 'docs-lock',
                state: flow(false),
                title: `Share with ${otherName}`,
                summary: 'Shared',
                body: (
                    <>
                        <p className="trail-note">Becomes the record of what this trade should contain.</p>
                        <div className="trail-actions">
                            <button className="cta-gold" disabled={busy || dead} onClick={submitDocs}>Share</button>
                        </div>
                    </>
                ),
            },
            {
                key: 'docs-review',
                state: !locked ? 'upcoming' : !docs.theirsSubmitted ? 'wait' : !docs.myAck ? 'active' : 'done',
                title: locked && !docs.theirsSubmitted ? `Waiting on ${otherName}'s photos` : `Check ${otherName}'s photos`,
                summary: 'Approved',
                body: docs.theirs && (
                    <>
                        <div className="ship-photo-grid">
                            {(docs.theirs.photos || []).map(p => (
                                <img key={p} src={p} alt={`${otherName}'s cards`} onClick={() => setLightbox(p)} />
                            ))}
                        </div>
                        <p className="ship-docs-desc-view">{docs.theirs.description}</p>
                        {reviewButtons('docs/ack', 'Looks right')}
                    </>
                ),
            },
            {
                key: 'docs-wait',
                state: docs.myAck ? 'wait' : 'upcoming',
                title: docs.myAck ? `Waiting on ${otherName} to approve yours` : `${otherName} approves yours`,
            },
        ];
    };

    /* ---------------- CHAPTER 4 · SHIP ---------------- */
    const tidyBinders = () => {
        setDialog({
            title: 'Update your binders?',
            message: 'Removes the cards you sent from your collection and the cards you received from your wishlist.',
            confirmLabel: 'Update binders',
            action: async () => {
                setBusy(true);
                try {
                    const res = await fetch(`/api/trade-requests/${tradeId}/binder-cleanup`, {
                        method: 'POST', credentials: 'include',
                    });
                    const json = await res.json();
                    if (res.status === 409) { setBinderCleaned(true); return; }
                    if (!res.ok) throw new Error(json.error || 'Failed to update binders.');
                    setBinderCleaned(true);
                    setNotice({
                        title: 'Binders updated',
                        message: `Removed ${json.removedHaves} card${json.removedHaves !== 1 ? 's' : ''} from your collection and ${json.removedWants} from your wishlist.`,
                    });
                } catch (e) {
                    setNotice({ title: 'Update failed', message: e.message });
                } finally {
                    setBusy(false);
                }
            },
        });
    };

    const shipNodes = () => {
        const nodes = [];
        // Peer-to-peer only: a private photo of the sealed package
        // unlocks the other trader's address
        const photoGate = !!data.packagePhoto;
        const hasPhoto = !!data.packagePhoto?.mine;
        const outSent = !!outLeg && outLeg.status !== 'ready';
        const inGot = inLeg?.status === 'delivered';
        const shopLegs = data.legs.filter(l => l.method === 'shop');
        const mm = shopLegs.length > 0;
        const allVerified = mm && shopLegs.every(l => VERIFIED_OR_LATER.includes(l.status));
        const pickup = inLeg?.dest_type === 'shop';
        const advance = (leg, action) => post('advance', { legId: leg.id, action });

        if (photoGate) {
            nodes.push({
                key: 'ship-photo',
                state: hasPhoto || outSent ? 'done' : 'active',
                title: 'Photograph your sealed package',
                summary: 'On file',
                body: (
                    <>
                        <p className="trail-note">Kept on file for this trade. Unlocks the address.</p>
                        <label className="ship-file-drop">
                            <input type="file" accept="image/*" onChange={e => setPkgFile(e.target.files[0] || null)} />
                            {pkgFile ? `${pkgFile.name} · tap to change` : 'Choose the photo'}
                        </label>
                        {pkgPreview && (
                            <div className="ship-photo-grid"><img src={pkgPreview} alt="package preview" /></div>
                        )}
                        <div className="trail-actions">
                            <button
                                className="cta-gold"
                                disabled={busy || !pkgFile || dead}
                                onClick={() => setDialog({
                                    title: 'Save your package photo?',
                                    message: 'It goes on file for this trade and cannot be replaced.',
                                    confirmLabel: 'Save photo',
                                    action: () => {
                                        const fd = new FormData();
                                        fd.append('photo', pkgFile);
                                        postForm('package-photo', fd).then(ok => { if (ok) setPkgFile(null); });
                                    },
                                })}
                            >
                                Save photo
                            </button>
                        </div>
                    </>
                ),
            });
        }

        if (outLeg) {
            const outShop = shopById(outLeg.shop_id);
            nodes.push({
                key: 'ship-send',
                state: outSent ? 'done' : (photoGate && !hasPhoto) ? 'upcoming' : 'active',
                title: mm ? 'Drop off your cards' : 'Mail your cards',
                summary: mm ? outShop?.name : 'Mailed',
                body: mm ? (
                    <>
                        <ShopCard shop={outShop} />
                        <div className="trail-actions">
                            <button className="cta-gold" disabled={busy || dead} onClick={() => advance(outLeg, 'shipped')}>
                                I dropped them off
                            </button>
                        </div>
                    </>
                ) : (
                    <>
                        <div className="trail-address">
                            <span>Mail to</span>
                            <strong>{outLeg.dest_resolved || outLeg.dest_address}</strong>
                        </div>
                        <div className="trail-actions">
                            <button className="cta-gold" disabled={busy || dead} onClick={() => advance(outLeg, 'shipped')}>
                                I mailed them
                            </button>
                        </div>
                    </>
                ),
            });
        }

        if (mm) {
            nodes.push({
                key: 'ship-verify',
                state: allVerified ? 'done' : outSent ? 'wait' : 'upcoming',
                title: outSent && !allVerified ? 'Middlemen are checking the cards' : 'Middlemen check the cards',
                summary: 'Both sides verified',
                body: (
                    <>
                        <div className="trail-tracks">
                            <MiniTrack leg={outLeg} label="Your cards" />
                            <MiniTrack leg={inLeg} label={`${otherName}'s cards`} />
                        </div>
                        <p className="trail-note">Nothing moves on until both sides check out.</p>
                    </>
                ),
            });
        }

        if (inLeg) {
            // Pickup happens at MY middleman (dest shop); mail comes from theirs
            const pickupShop = shopById(inLeg.dest_shop_id);
            const canGet = !mm ? inLeg.status === 'shipped'
                : !pickup ? inLeg.status === 'forwarded'
                    : pickupElsewhere(inLeg) ? inLeg.status === 'at_pickup'
                        : inLeg.status === 'shop_verified' && allVerified;
            // Nothing for me to do yet, but the ball is rolling
            const inTransit = !inGot && !canGet && (mm ? allVerified : outSent);
            nodes.push({
                key: 'ship-receive',
                state: inGot ? 'done' : canGet ? 'active' : inTransit ? 'wait' : 'upcoming',
                title: inTransit
                    ? (!mm ? `Waiting on ${otherName} to mail theirs`
                        : pickup ? `On the way to ${pickupShop?.name || 'your middleman'}`
                            : 'The middleman is mailing your cards')
                    : pickup ? 'Pick up your cards'
                        : canGet ? 'Your cards are on the way' : 'Get your cards',
                summary: pickup ? 'Picked up' : 'Received',
                body: canGet && pickup ? (
                    <>
                        <ShopCard shop={pickupShop} />
                        <div className="trail-actions">
                            <button className="cta-gold" disabled={busy || dead} onClick={() => advance(inLeg, 'delivered')}>
                                I picked them up
                            </button>
                        </div>
                    </>
                ) : canGet ? (
                    <>
                        <MiniTrack leg={inLeg} label={`${otherName}'s cards`} />
                        {(inLeg.dest_resolved || inLeg.dest_address) && (
                            <p className="trail-fact">Coming to {inLeg.dest_resolved || inLeg.dest_address}</p>
                        )}
                        <div className="trail-actions">
                            <button className="cta-gold" disabled={busy || dead} onClick={() => advance(inLeg, 'delivered')}>
                                I got them
                            </button>
                        </div>
                    </>
                ) : (
                    <MiniTrack leg={inLeg} label={`${otherName}'s cards`} />
                ),
            });
        }

        nodes.push({
            key: 'ship-wait',
            state: finished ? 'done' : (outSent && inGot) ? 'wait' : 'upcoming',
            title: !finished && outSent && inGot ? `Waiting on ${otherName} to get theirs` : `${otherName} gets theirs`,
            body: <MiniTrack leg={outLeg} label="Your cards" />,
        });

        const toRate = data.canRate ? ratableShopIds : [];
        const needsRating = toRate.some(id => !isRated(id));
        const wrapState = finished
            ? (needsRating || !bindersDone ? 'active' : 'done')
            : needsRating ? 'active' : 'upcoming';
        nodes.push({
            key: 'ship-wrap',
            state: wrapState,
            title: finished ? 'Trade complete' : 'Wrap up',
            summary: 'All wrapped up',
            body: (
                <>
                    {toRate.length > 0 && (
                        <div className="trail-wrap-block">
                            <div className="ship-field-label">Rate your middlem{toRate.length > 1 ? 'e' : 'a'}n</div>
                            {toRate.map(id => (
                                <ReviewShop
                                    key={id}
                                    shopId={id}
                                    shopName={shopById(id)?.name || `Shop #${id}`}
                                    tradeId={tradeId}
                                    done={isRated(id)}
                                    onDone={markRated}
                                />
                            ))}
                        </div>
                    )}
                    {finished && (
                        <div className="trail-wrap-block">
                            <div className="ship-rate-row">
                                <span>Update my binders</span>
                                {bindersDone
                                    ? <span className="ship-hint">✓ Updated</span>
                                    : <button className="cta-gold ship-rate-submit" disabled={busy} onClick={tidyBinders}>Update</button>}
                            </div>
                            <p className="trail-note">Takes sent cards out of your collection and received ones off your wishlist.</p>
                        </div>
                    )}
                </>
            ),
        });
        return nodes;
    };

    const setupMine = data.setup?.mine;
    const chapters = [
        {
            key: 'decide', step: 1, label: 'Decide', build: decideNodes,
            summary: data.middlemanRequired ? 'Middleman trade' : 'Direct trade',
        },
        {
            key: 'setup', step: 2, label: 'Set up', build: setupNodes,
            summary: data.middlemanRequired && setupMine
                ? [shopById(setupMine.shopId)?.name, setupMine.pickup ? 'pick up' : 'mailed to you'].filter(Boolean).join(' · ')
                : 'Addresses set',
        },
        ...(data.docsRequired ? [{
            key: 'docs', step: 3, label: 'Photos', build: docsNodes, summary: 'Both approved',
        }] : []),
        { key: 'ship', step: 4, label: 'Ship', build: shipNodes, summary: 'Done' },
    ];
    const rejected = data.legs.some(l => l.status === 'shop_rejected');

    return (
        <div className="requests-container ship-page">
            <header className="page-section-head ship-head">
                <h1>
                    Trade <span className="page-section-current">Shipping with {otherName}</span>
                    {data.tradeCode && <span className="ship-trade-code">{data.tradeCode}</span>}
                </h1>
                {otherId && (
                    <button className="ship-msg-btn" onClick={() => navigate(`/messages?to=${otherId}`)}>
                        <Icon name="chat" /> Message {otherName}
                    </button>
                )}
            </header>

            {dead && (
                <div className="ship-rejected-banner trail-banner">
                    {rejected
                        ? 'A middleman rejected a package, so this trade was cancelled.'
                        : 'This trade was cancelled. You\'re viewing it as it stood.'}
                </div>
            )}

            <ol className="trail">
                {chapters.flatMap((ch, i) => {
                    const isCurrent = ch.step === data.step;
                    const headState = ch.step < data.step || (isCurrent && finished) ? 'done'
                        : isCurrent ? 'current' : 'upcoming';
                    const items = [(
                        <TrailChapter
                            key={ch.key}
                            n={i + 1}
                            label={ch.label}
                            state={headState}
                            summary={headState === 'done' && !isCurrent ? ch.summary : null}
                        />
                    )];
                    if (isCurrent) {
                        ch.build().forEach(node => items.push(<TrailNode key={node.key} node={node} />));
                    }
                    return items;
                })}
            </ol>

            {!finished && !dead && data.step < 4 && (
                <div className="trail-dropout">
                    <button className="trail-deny" disabled={busy} onClick={dropOut}>Drop out of this trade</button>
                </div>
            )}

            {lightbox && (
                <div className="ship-lightbox" onClick={() => setLightbox(null)}>
                    <img src={lightbox} alt="trade documentation" />
                </div>
            )}

            <ConfirmDialog
                open={!!dialog}
                title={dialog?.title}
                message={dialog?.message}
                confirmLabel={dialog?.confirmLabel}
                danger={dialog?.danger}
                busy={busy}
                onConfirm={() => { const a = dialog.action; setDialog(null); a(); }}
                onCancel={() => setDialog(null)}
            />
            <ConfirmDialog
                open={!!notice}
                title={notice?.title}
                message={notice?.message}
                onCancel={() => { const c = notice?.onClose; setNotice(null); if (c) c(); }}
            />

        </div>
    );
}

export default Shipping;
